import { SectionData, TeaserData } from "@app/types/ServerCustomContext";
import AnchorCarousel from "@components/Modules/AnchorCarousel/AnchorCarousel";
import AppExploreMore from "@components/Modules/AppExploreMore/AppExploreMore";
import VideoSliderPortrait from "@components/Modules/VideoSliderPortrait/VideoSliderPortrait";
import {
  configCategory,
  type ConfigCategoryLayoutElement,
  configExploreMore,
  configTeaser,
} from "@pub/config";
import placeholders from "@pub/placeholders";
import {
  Ads,
  AnchorBlock,
  Arrow,
  Card,
  CustomClassContext,
  Divider,
  ExploreMore,
  ListGroup,
  SectionContext,
  Tab,
  Typography,
  UserSubmissionBox,
  VideoGalleryPortrait,
} from "@sphtech/dmg-design-system";
import { NoSSR } from "@sphtech/web2-core/components";
import cx from "classnames";
import { Fragment, ReactNode, useContext } from "react";

import type { ResolvedFieldValue } from "../Article/elements";
import { Annotation } from "../Article/StorylineElements/Annotation/Annotation";
import styles from "./category.module.css";
import CategoryStoryListing from "./CategoryStoryListing";
import CategoryStoryListingLayout from "./CategoryStoryListingLayout";
import SubAnchor from "./SubAnchor";
import SubAnchorVideo from "./SubAnchorVideo";

const { Heading, Label } = Typography;

type AnchorBlockProps = React.ComponentProps<typeof AnchorBlock> & {
  id?: string;
};

type videoGalleryLandscapeProps = {
  title?: string;
  teasers: TeaserData[];
};

type Props = {
  data: {
    uniqSectionName?: string;
    tagSlug?: string;
    name: string;
    nameZH?: string;
    subHeading?: string;
    intro?: {
      description?: ResolvedFieldValue;
    };
    listGroup?: React.ComponentProps<typeof ListGroup>;
    anchorCarousel?: TeaserData[];
    anchorBlock?: AnchorBlockProps;
    latestArticle?: TeaserData[];
    exploreMore?: React.ComponentProps<typeof ExploreMore>;
    videoGallery?: videoGalleryLandscapeProps;
    socialVideoBlock?: SectionData["socialVideoBlock"];
    mainContentBlock?: TeaserData[];
    lb1?: ReactNode;
    imu1?: ReactNode;
    imu2?: ReactNode;
    userSubmissionBox?: UserSubmissionBox;
    queryLegacyTagData?: boolean;
    storyType?: SectionData["storyType"];
    path?: SectionData["path"];
  };
};

// Default layout elements order for category page.
// This can be removed once all publications have defined their own layout in the config.
const defaultLayoutElements: ConfigCategoryLayoutElement[] = [
  { name: "introBlock" },
  { name: "anchorBlock" },
  { name: "subAnchorBlockWithVideo" },
  { name: "storyListingBlock" },
];

function CategoryLayout({ data }: Props): ReactNode {
  const uniqueSectionNameOrTag = data.uniqSectionName ?? data.tagSlug;
  const ignoreIds = [] as string[];
  const isSectionPage = !!data.uniqSectionName;
  const hasVideoGallery = !!(
    configCategory.videoGalleryLandscape?.display &&
    data.videoGallery &&
    data.videoGallery.teasers.length > 0
  );
  const mobileAdBeforeVideoGallery = isSectionPage && hasVideoGallery;

  if (data.anchorBlock?.id) {
    ignoreIds.push(data.anchorBlock.id);
  }

  if (data.latestArticle) {
    data.latestArticle.forEach((item) => {
      if (item.id) {
        ignoreIds.push(item.id);
      }
    });
  }

  if (data.mainContentBlock) {
    data.mainContentBlock.forEach((item) => {
      if (item.id) {
        ignoreIds.push(item.id);
      }
    });
  }

  const customClass = useContext(CustomClassContext);
  const sectionContainerClass = customClass?.categoryLayout?.sectionContainer;
  const infoContainerClass = customClass?.categoryLayout?.infoContainer;
  const categoryLayout = configCategory.card;
  const tabPath =
    data.path || (data.tagSlug && `/tag/${data.tagSlug}`) || "/latest-stories";
  const layoutElements =
    (data.path && configCategory.layoutGridByPath?.[data.path]) ||
    configCategory.layoutGrid ||
    defaultLayoutElements;

  const introBlock = (
    <section
      className={cx(
        styles.bodyContainer,
        styles.categoryInfoContainer,
        sectionContainerClass,
        infoContainerClass,
      )}
    >
      {data.name && (
        <div
          className={cx(
            styles.headingContainer,
            data.listGroup || data.intro?.description?.value
              ? styles.headingWithTab
              : styles.headingStandalone,
            customClass?.categoryLayout?.categoryHeading,
            data.tagSlug && customClass?.tagLayout?.headingContainer,
          )}
        >
          {data.subHeading && <Label Element="span">{data.subHeading}</Label>}

          <Heading.SectionTitle nameZH={data.nameZH}>
            {data.name}
          </Heading.SectionTitle>
        </div>
      )}

      {data.intro?.description?.value && (
        <div className={cx(styles.categoryDescriptionContainer)}>
          <Typography.Body>
            <Annotation element={data.intro.description} />
          </Typography.Body>
        </div>
      )}

      {data.listGroup && (
        <div
          className={cx(
            styles.tabContainer,
            customClass?.categoryLayout?.tabContainer,
          )}
        >
          <ListGroup
            variant={configCategory.listGroupVariant}
            {...data.listGroup}
          />

          {!configCategory.hideSubmenuDivider && <Divider />}
        </div>
      )}

      {!!configCategory.filterBy?.options?.length && (
        <Tab
          label={configCategory.filterBy.label}
          data={configCategory.filterBy.options.map((option) => {
            const isSelected = data.storyType === option.storyType;
            return {
              text: option.text,
              url: isSelected ? tabPath : `${tabPath}/${option.pathSuffix}`,
              isSelected,
            };
          })}
        />
      )}
    </section>
  );

  const anchorBlock = data.anchorBlock ? (
    <section
      className={cx(
        styles.bodyContainer,
        styles.gutterBottomOnly,
        styles.categoryAnchorBlockContainer,
        sectionContainerClass,
        customClass?.categoryLayout?.anchorBlock,
      )}
    >
      {categoryLayout ? (
        <Card
          {...data.anchorBlock}
          variant={categoryLayout.anchorblock.defaultVariant}
          mobileUpVariant={categoryLayout.anchorblock.mobileUp.defaultVariant}
          placeholders={placeholders}
          displayPublishDate={configTeaser.anchorBlock?.publishDate}
        />
      ) : (
        <AnchorBlock
          {...data.anchorBlock}
          placeholder={placeholders.landscape}
          displayPublishDate={configTeaser.anchorBlock?.publishDate}
          mobileCrop={configTeaser.anchorBlock?.mobileCrop}
        />
      )}
    </section>
  ) : null;

  const subAnchorBlock = data.latestArticle?.length ? (
    <section className={cx(styles.bodyContainer, sectionContainerClass)}>
      <div className={styles.latestTeaserContainer}>
        <div className={styles.latestTeaserLayout}>
          <SubAnchor articleList={data.latestArticle} />
          {mobileAdBeforeVideoGallery && (
            <div className={styles.mobileAdvertisement}>
              <Ads.AdsSideBarMobile />
            </div>
          )}
        </div>

        <div className={styles.sidebar}>
          {mobileAdBeforeVideoGallery ? (
            <Ads.AdsSideBarDesktop />
          ) : (
            <Ads.AdsSideBar />
          )}

          {configCategory.exploreMoreInPinned && uniqueSectionNameOrTag && (
            <div className={styles.pinnedExploreMore}>
              <NoSSR>
                <SectionContext.Provider value={{}}>
                  <AppExploreMore
                    uniqSectionName={data.uniqSectionName}
                    tagName={data.tagSlug}
                    headingText={configExploreMore.heading}
                    headingTextZH={configExploreMore.headingZH}
                    render={(children) => (
                      <div
                        className={cx(
                          styles.exploreMoreContainer,
                          customClass?.categoryLayout?.exploreMoreContainer,
                        )}
                      >
                        {!configExploreMore.hideDivider && <Divider />}
                        {children}
                        {!configExploreMore.hideDivider && <Divider />}
                      </div>
                    )}
                  />
                </SectionContext.Provider>
              </NoSSR>
            </div>
          )}
        </div>
      </div>
    </section>
  ) : null;

  const subAnchorBlockWithVideo = data.latestArticle?.length ? (
    <section
      className={cx(
        styles.bodyContainer,
        styles.gutterBottom,
        sectionContainerClass,
      )}
    >
      <div className={styles.latestTeaserContainer}>
        <div className={styles.latestTeaserLayout}>
          <SubAnchor articleList={data.latestArticle} />
          {mobileAdBeforeVideoGallery && (
            <div className={styles.mobileAdvertisement}>
              <Ads.AdsSideBarMobile />
            </div>
          )}
          {hasVideoGallery && (
            <div>
              <SubAnchorVideo videoGallery={data.videoGallery} />
            </div>
          )}
        </div>

        <div className={styles.sidebar}>
          {mobileAdBeforeVideoGallery ? (
            <Ads.AdsSideBarDesktop />
          ) : (
            <Ads.AdsSideBar />
          )}

          {configCategory.exploreMoreInPinned && uniqueSectionNameOrTag && (
            <div className={styles.pinnedExploreMore}>
              <NoSSR>
                <SectionContext.Provider value={{}}>
                  <AppExploreMore
                    uniqSectionName={data.uniqSectionName}
                    tagName={data.tagSlug}
                    headingText={configExploreMore.heading}
                    headingTextZH={configExploreMore.headingZH}
                    render={(children) => (
                      <div
                        className={cx(
                          styles.exploreMoreContainer,
                          customClass?.categoryLayout?.exploreMoreContainer,
                        )}
                      >
                        {!configExploreMore.hideDivider && <Divider />}
                        {children}
                        {!configExploreMore.hideDivider && <Divider />}
                      </div>
                    )}
                  />
                </SectionContext.Provider>
              </NoSSR>
            </div>
          )}
        </div>
      </div>
    </section>
  ) : null;

  const videoGalleryBlock = hasVideoGallery ? (
    <section className={cx(styles.bodyContainer, styles.videoGallerySection)}>
      <div className={styles.videoGalleryContainer}>
        <div className={styles.videoGalleryWrapper}>
          <SubAnchorVideo videoGallery={data.videoGallery} />
          {mobileAdBeforeVideoGallery && (
            <div className={styles.mobileAdvertisement}>
              <Ads.AdsSideBarMobile />
            </div>
          )}
        </div>

        <div className={styles.sidebar}>
          {mobileAdBeforeVideoGallery ? (
            <Ads.AdsSideBarDesktop />
          ) : (
            <Ads.AdsSideBar />
          )}
        </div>
      </div>
    </section>
  ) : null;

  const socialVideoPortraitBlock =
    configCategory.socialVideoPortrait?.display &&
    data.socialVideoBlock &&
    data.socialVideoBlock.teasers.length > 0 ? (
      configCategory.socialVideoPortrait.variant === "slider" ? (
        <section
          className={cx(
            styles.bodyContainer,
            styles.socialVideoContainer,
            sectionContainerClass,
          )}
        >
          <VideoSliderPortrait
            title={data.socialVideoBlock.title}
            titleZH={data.socialVideoBlock.titleZH}
            cta={{
              url: data.socialVideoBlock.url,
              headingIcon: configCategory.socialVideoPortrait.viewMore?.icon,
            }}
            items={data.socialVideoBlock.teasers.map((teaser) => ({
              caption: "",
              videoUrl: teaser.embedUri,
            }))}
          />
        </section>
      ) : (
        <section
          className={cx(styles.bodyContainer, styles.socialVideoContainer)}
        >
          <VideoGalleryPortrait
            title={data.socialVideoBlock.title}
            items={data.socialVideoBlock.teasers.map((teaser) => ({
              caption: "",
              videoUrl: teaser.embedUri,
            }))}
            leftIcon={<Arrow size={54} direction="left" />}
            rightIcon={<Arrow size={54} />}
            imgWidth="250px"
            imgHeight="450px"
            noOfItemsInView={3}
            description=""
            withDivider={false}
          />
        </section>
      )
    ) : null;

  const anchorCarouselBlock = data.anchorCarousel ? (
    <div className={styles.categoryCarouselContainer}>
      <AnchorCarousel data={data.anchorCarousel} />

      <div className={styles.mobileAdvertisement}>
        <Ads.AdsSideBarMobile />
      </div>
    </div>
  ) : null;

  const storyListingBlock =
    data.mainContentBlock && data.mainContentBlock.length > 0 ? (
      <CategoryStoryListingLayout
        data={{
          mainContentBlock: data.mainContentBlock,
          imu: data.imu1,
        }}
      />
    ) : null;

  const layoutSections: Record<ConfigCategoryLayoutElement["name"], ReactNode> =
    {
      introBlock: introBlock,
      anchorBlock: anchorBlock,
      subAnchorBlock: subAnchorBlock,
      subAnchorBlockWithVideo: subAnchorBlockWithVideo,
      videoGalleryBlock: videoGalleryBlock,
      socialVideoPortraitBlock: socialVideoPortraitBlock,
      anchorCarousel: anchorCarouselBlock,
      storyListingBlock: storyListingBlock,
    };

  return (
    <Fragment>
      {layoutElements.map((element, index) => (
        <Fragment key={index}>{layoutSections[element.name]}</Fragment>
      ))}

      <NoSSR>
        <CategoryStoryListing
          data={{
            uniqSectionName: data.uniqSectionName,
            tagSlug: data.tagSlug,
            limit: 8,
            ignoreIds: ignoreIds,
            lb1: data.lb1,
            imu: data.imu2,
            userSubmissionBox: data.userSubmissionBox,
            queryLegacyTagData: data.queryLegacyTagData,
            storyType: data.storyType,
          }}
        />
      </NoSSR>
    </Fragment>
  );
}

export default CategoryLayout;
